import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkle, DownloadSimple, Copy, ArrowRight, CheckCircle, GitDiff } from "@phosphor-icons/react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import api from "@/lib/api";

function wrap(ctx, text, x, y, maxWidth, lineHeight) {
  const words = (text || "").split(" ");
  let line = "";
  let yy = y;
  for (const w of words) {
    const test = line + w + " ";
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line.trim(), x, yy);
      line = w + " ";
      yy += lineHeight;
    } else {
      line = test;
    }
  }
  ctx.fillText(line.trim(), x, yy);
  return yy;
}

export default function Share() {
  const { resultId } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/share/${resultId}`).then(({ data }) => setData(data)).catch(() => setNotFound(true));
  }, [resultId]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const downloadImage = () => {
    const c = canvasRef.current || document.createElement("canvas");
    c.width = 1080; c.height = 1080;
    const ctx = c.getContext("2d");
    const color = data.primary.color || "#14B8A6";
    ctx.fillStyle = "#020617";
    ctx.fillRect(0, 0, 1080, 1080);
    const grad = ctx.createRadialGradient(900, 180, 60, 900, 180, 700);
    grad.addColorStop(0, color + "55");
    grad.addColorStop(1, "#02061700");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 1080, 1080);

    ctx.fillStyle = "#5eead4";
    ctx.font = "bold 28px Arial";
    ctx.fillText("THEINNER.APP", 90, 130);

    ctx.fillStyle = "#64748b";
    ctx.font = "24px Arial";
    ctx.fillText((data.assessment_name || "").toUpperCase(), 90, 300);

    ctx.fillStyle = "#94a3b8";
    ctx.font = "30px Arial";
    ctx.fillText("I am", 90, 380);

    ctx.fillStyle = color;
    ctx.font = "bold 88px Arial";
    wrap(ctx, data.primary.name, 90, 480, 900, 100);

    ctx.fillStyle = "#cbd5e1";
    ctx.font = "italic 36px Arial";
    wrap(ctx, "“" + data.primary.tagline + "”", 90, 660, 900, 50);

    ctx.fillStyle = "#94a3b8";
    ctx.font = "30px Arial";
    wrap(ctx, data.primary.summary, 90, 800, 900, 46);

    ctx.fillStyle = "#5eead4";
    ctx.font = "26px Arial";
    ctx.fillText("Discover your pattern → theinner.app", 90, 1010);

    const url = c.toDataURL("image/png");
    const link = document.createElement("a");
    link.download = `theinner-${data.slug || "result"}.png`;
    link.href = url;
    link.click();
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-950"><Navbar />
        <div className="mx-auto max-w-xl px-5 pt-40 text-center">
          <h1 className="font-heading text-3xl text-slate-100">This shared result isn't available</h1>
          <Link to="/" className="mt-4 inline-block text-teal-400">← Explore the free assessments</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-950"><div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-800 border-t-teal-400" /></div>;
  }

  const color = data.primary.color;

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <div className="mx-auto max-w-3xl px-5 pb-24 pt-32 sm:px-8" data-testid="share-page">
        <p className="text-center text-xs font-bold uppercase tracking-[0.2em] text-teal-500">A pattern from {data.brand}</p>

        {/* Shareable card */}
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="relative mt-6 overflow-hidden rounded-3xl border border-slate-800 bg-slate-900 p-8 sm:p-12" data-testid="share-card">
          <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full opacity-30 blur-3xl" style={{ background: color }} />
          <div className="relative">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">{data.assessment_name}</span>
            <p className="mt-4 text-sm text-slate-400">I am</p>
            <h1 className="mt-1 font-heading text-4xl font-light tracking-tight sm:text-6xl" style={{ color }} data-testid="share-primary-name">{data.primary.name}</h1>
            <p className="mt-3 text-lg italic text-slate-400">&quot;{data.primary.tagline}&quot;</p>
            <p className="mt-6 text-base leading-relaxed text-slate-300">{data.primary.summary}</p>
          </div>
        </motion.div>

        {/* Actions */}
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <button onClick={downloadImage} className="inline-flex items-center gap-2 rounded-full bg-teal-500 px-6 py-3 text-sm font-semibold text-slate-950 hover:bg-teal-400" data-testid="share-download-btn">
            <DownloadSimple size={16} weight="bold" /> Download image
          </button>
          <button onClick={copyLink} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-6 py-3 text-sm font-medium text-slate-200 hover:border-slate-500" data-testid="share-copy-btn">
            {copied ? <><CheckCircle size={16} weight="fill" className="text-teal-400" /> Copied</> : <><Copy size={16} /> Copy link</>}
          </button>
        </div>

        {/* CTA to take the assessment */}
        <div className="mt-10 rounded-2xl border p-7 text-center" style={{ borderColor: `${color}40`, background: `${color}0d` }} data-testid="share-cta">
          <Sparkle size={28} weight="duotone" className="mx-auto" style={{ color }} />
          <h2 className="mt-3 font-heading text-2xl font-light text-slate-100">What's your pattern?</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-400">Take the free {data.assessment_name} and map the hidden pattern running this part of your life.</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Link to={`/a/${data.slug}`} className="inline-flex items-center gap-2 rounded-full bg-teal-500 px-6 py-3 text-sm font-semibold text-slate-950 hover:bg-teal-400" data-testid="share-take-btn">Take this free assessment <ArrowRight size={15} weight="bold" /></Link>
            <Link to={`/compare?b=${resultId}`} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-6 py-3 text-sm font-medium text-slate-200 hover:border-slate-500" data-testid="share-compare-btn"><GitDiff size={15} weight="duotone" /> Compare with yours</Link>
          </div>
        </div>

        <canvas ref={canvasRef} className="hidden" />
      </div>
      <Footer />
    </div>
  );
}
